import { BaseDetector, DetectorType } from '@src/slide/application/service/snap-line-service/detector/base';
import {
    LineType,
} from '@src/slide/application/service/snap-line-service/interface/line.interface';
import { RefSourceType } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import { Orient } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import type {
    IActiveSnapGridLine, ISnapGridLine } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import type { IRefElement } from '@src/slide/application/service/snap-line-service/interface/ref.interface';

/**
 * 宽高探测器
 */
export class RectDetector extends BaseDetector {
    public type = DetectorType.DYNAMIC;

    private heightRefGroups: Record<string, IRefElement[]> = {};
    private widthRefGroups: Record<string, IRefElement[]> = {};

    public detect(): IActiveSnapGridLine[] {
        const { rotate = 0 } = this.context.nextSizeOnSlide;
        // 宽高这里需要再完善一下
        // 在有角度的resize时，再操作一个锚点的时候，会同时改变他的宽高
        // 不能单纯的用 垂直、水平吸附去目标性的改变其rect
        // 可能还涉及到投影以及 rect范围判定。规则比较复杂
        if (rotate % 90 !== 0 || this.context.noChangeAspect) {
            return [];
        }

        return super.detect();
    }

    protected processActiveSnapLines(activeSnapLines: IActiveSnapGridLine[]): IActiveSnapGridLine[] {
        // 在触发吸附的情况，把自身也加入到refs中，提供给 render 在自身上也渲染一个 rect参考线
        activeSnapLines.forEach(snapLine => snapLine.refElements.push(this.createRefWithNextFrame(activeSnapLines)));

        return activeSnapLines;
    }

    protected collectRefGrids(): ISnapGridLine[] {
        this.resetRectGroup();
        this.collectRectGroups();
        return [...this.getHeightRefGrids(), ...this.getWidthRefGrids()];
    }

    private createSnapLine(snapLineOpt: Partial<ISnapGridLine>): ISnapGridLine {
        const snapLine = {
            ...snapLineOpt,
            maxAdsorbGap: this.context.option.maxAdsorbGap,
            type: LineType.RECT,
            source: RefSourceType.ELEMENT,
        };

        return snapLine as ISnapGridLine;
    }

    private createRefWithNextFrame(activeSnapLines: IActiveSnapGridLine[]): IRefElement {
        const { operationAnchorOnSlide, detectFrame } = this.context;
        // 同时只会有一根吸附
        const frame = activeSnapLines.reduce((nextFrame, activeSnapLine) => {
            const frameInfo = this.getRefFrame(nextFrame);
            if (activeSnapLine.orient === Orient.VERTICAL) {
                if (operationAnchorOnSlide.includes('left')) {
                    const left = activeSnapLine.position;
                    return { ...nextFrame, left, width: frameInfo.right - left };
                }
                if (operationAnchorOnSlide.includes('right')) {
                    const right = activeSnapLine.position;
                    return { ...nextFrame, width: right - frameInfo.left };
                }
            } else {
                if (operationAnchorOnSlide.includes('top')) {
                    const top = activeSnapLine.position;
                    return { ...nextFrame, top, height: frameInfo.bottom - top };
                }
                if (operationAnchorOnSlide.includes('bottom')) {
                    const bottom = activeSnapLine.position;
                    return { ...nextFrame, height: bottom - frameInfo.top };
                }
            }

            return nextFrame;
        }, detectFrame);

        return {
            ele: { props: { style: { ...frame, rotate: 0 } } },
            source: RefSourceType.ELEMENT,
            outerFrameOnSlide: frame,
        };
    }

    private getHeightRefGrids() {
        const { operationAnchorOnSlide, detectFrame } = this.context;
        const heightSnapLines: ISnapGridLine[] = [];
        const heightRefList = Object.keys(this.heightRefGroups);
        // 90%整数，就有两边以上不变，这个时候宽高都吸附
        // 非整数，基本只有一个点不变，这个时候，根据 movingDirection，判断是计算高的点不变还是计算宽的点不变。只吸附一侧
        heightRefList.forEach((height) => {
            const refs = this.heightRefGroups[height];
            if (operationAnchorOnSlide.includes('top')) {
                // 基于 frame 往上扩充吸附线
                const bottom = detectFrame.top + detectFrame.height;
                heightSnapLines.push(this.createSnapLine({
                    orient: Orient.HORIZONTAL,
                    position: bottom - Number(height),
                    refElements: refs,
                }));
            }
            if (operationAnchorOnSlide.includes('bottom')) {
                // 基于 frame 往下扩充吸附线
                heightSnapLines.push(this.createSnapLine({
                    orient: Orient.HORIZONTAL,
                    position: detectFrame.top + Number(height),
                    refElements: refs,
                }));
            }
        });

        return heightSnapLines;
    }

    private getWidthRefGrids() {
        const { operationAnchorOnSlide, detectFrame } = this.context;
        const widthSnapLines: ISnapGridLine[] = [];
        const widthRefList = Object.keys(this.widthRefGroups);
        widthRefList.forEach((width) => {
            const refs = this.widthRefGroups[width];
            if (operationAnchorOnSlide.includes('left')) {
                // 基于 frame 往上扩充吸附线
                const right = detectFrame.left + detectFrame.width;
                widthSnapLines.push(this.createSnapLine({
                    orient: Orient.VERTICAL,
                    position: right - Number(width),
                    refElements: refs,
                }));
            }
            if (operationAnchorOnSlide.includes('right')) {
                // 基于 frame 往下扩充吸附线
                widthSnapLines.push(this.createSnapLine({
                    orient: Orient.VERTICAL,
                    position: detectFrame.left + Number(width),
                    refElements: refs,
                }));
            }
        });

        return widthSnapLines;
    }

    private collectRectGroups() {
        this.dynamicRefs.forEach((ref) => {
            const refOuterFrame = ref.outerFrameOnSlide;
            const height = refOuterFrame.height.toFixed(2);
            const width = refOuterFrame.width.toFixed(2);

            if (!this.heightRefGroups[height]) {
                this.heightRefGroups[height] = [];
            }
            this.heightRefGroups[height].push(ref);

            if (!this.widthRefGroups[width]) {
                this.widthRefGroups[width] = [];
            }
            this.widthRefGroups[width].push(ref);
        });
    }

    private get dynamicRefs() {
        return this.context.dynamicRefer.refs;
    }

    private resetRectGroup() {
        this.heightRefGroups = {};
        this.widthRefGroups = {};
    }
}
